import { useEffect, useState } from "react";
import { useCountUp } from "@/hooks/useCountUp";
import { Monitor, Users, Target } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

type Accent = "blue" | "violet" | "emerald" | "amber";

const accentMap: Record<Accent, { tile: string; spark: string; glow: string }> = {
  blue: {
    tile: "from-[hsl(217_91%_60%)] to-[hsl(213_94%_68%)]",
    spark: "hsl(217 91% 60%)",
    glow: "hsl(217 91% 60% / 0.18)",
  },
  violet: {
    tile: "from-[hsl(262_83%_65%)] to-[hsl(280_85%_70%)]",
    spark: "hsl(262 83% 65%)",
    glow: "hsl(262 83% 65% / 0.18)",
  },
  emerald: {
    tile: "from-[hsl(158_64%_52%)] to-[hsl(168_70%_52%)]",
    spark: "hsl(158 64% 52%)",
    glow: "hsl(158 64% 52% / 0.18)",
  },
  amber: {
    tile: "from-[hsl(32_95%_60%)] to-[hsl(20_95%_62%)]",
    spark: "hsl(32 95% 60%)",
    glow: "hsl(32 95% 60% / 0.18)",
  },
};

const sparkPaths = [
  "M0,40 L20,38 L40,30 L60,32 L80,22 L100,18 L120,14 L140,10",
  "M0,30 L25,32 L45,24 L70,28 L95,18 L120,22 L140,12",
  "M0,28 L20,30 L40,22 L60,26 L80,18 L100,20 L120,12 L140,8",
  "M0,32 L20,28 L40,26 L60,18 L80,22 L100,14 L120,10 L140,6",
];

export const LiveMetricsStrip = () => {
  const { ref } = useRevealOnScroll<HTMLDivElement>();
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setEnabled(true), 200);
    return () => window.clearTimeout(id);
  }, []);

  const v1 = useCountUp(4500, { enabled, duration: 1800 });
  const v2 = useCountUp(48, { enabled, duration: 1500, decimals: 1 });
  const v3 = useCountUp(92, { enabled, duration: 1400 });
  const v4 = useCountUp(38, { enabled, duration: 1400 });

  const metrics: Array<{
    icon: typeof Monitor;
    accent: Accent;
    value: string;
    label: string;
    delta: string;
    path: string;
  }> = [
    { icon: Monitor, accent: "blue", value: `${v1.toLocaleString()}+`, label: "Active Faces", delta: "+15.7%", path: sparkPaths[0] },
    { icon: Users, accent: "violet", value: `${(v2 / 10).toFixed(1)}M+`, label: "Daily Reach", delta: "+21.3%", path: sparkPaths[1] },
    { icon: Target, accent: "emerald", value: `${v3}%`, label: "AI Accuracy", delta: "+8.4%", path: sparkPaths[2] },
  ];

  return (
    <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 reveal-on-scroll">
      {metrics.map((m, i) => {
        const Icon = m.icon;
        const a = accentMap[m.accent];
        return (
          <div
            key={i}
            className="relative bg-card border border-border/60 rounded-2xl p-5 shadow-card hover:-translate-y-1 hover:shadow-stat transition-all duration-300 overflow-hidden"
          >
            <div
              className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-2xl pointer-events-none"
              style={{ background: a.glow }}
            />
            <div className="relative flex items-start justify-between gap-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${a.tile} flex items-center justify-center shadow-lg`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <svg viewBox="0 0 140 50" className="w-24 h-10 opacity-90">
                <path d={m.path} fill="none" stroke={a.spark} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="relative mt-3">
              <div className="text-3xl font-bold text-foreground leading-none tabular-nums">{m.value}</div>
              <div className="text-sm text-muted-foreground font-medium mt-1.5">{m.label}</div>
              <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold mt-1">{m.delta}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
